import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      const sections = ['home', 'features', 'contact', 'privacy', 'terms', 'support'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/1e75ead3-972b-4521-afb5-18f9884529eb.png" alt="EMEL S.R.L." className="h-10 w-10 object-contain" />
              <span className={`text-xl font-bold tracking-wide ${isScrolled ? 'text-gray-900' : 'text-white'}`}>EMEL S.R.L.</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              {[
                { id: 'home', label: 'Home' },
                { id: 'features', label: 'VPN Features' },
                { id: 'contact', label: 'Contact' },
                { id: 'privacy', label: 'Privacy Policy' },
                { id: 'terms', label: 'Terms of Service' },
                { id: 'support', label: 'Support' }
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white/90 hover:text-white'
                  } ${activeSection === item.id ? (isScrolled ? 'text-teal-500' : 'text-white') : ''}`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button 
              onClick={() => scrollToSection('contact')}
              className="hidden md:block bg-white text-gray-900 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-teal-900 to-gray-800"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-6xl md:text-7xl font-bold text-white leading-tight mb-8">
                Secure Your<br />Digital Freedom
              </h1>
              <p className="text-lg text-white/80 mb-10 max-w-md">
                Enterprise-grade VPN protection for your privacy and security online
              </p>
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-teal-500 text-white px-10 py-4 rounded-full text-base font-medium hover:bg-teal-600 transition-all shadow-lg hover:shadow-teal-500/50 whitespace-nowrap cursor-pointer"
              >
                Start Protecting Now
              </button>
            </div>
            
            <div className="hidden md:flex items-center justify-center">
              <div className="relative w-96 h-96">
                <div className="absolute inset-0 bg-teal-500/20 rounded-full blur-3xl"></div>
                <div className="relative flex items-center justify-center h-full">
                  <i className="ri-shield-check-line text-white text-9xl opacity-90"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-600 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-6">
              <i className="ri-star-line"></i>
              Features
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Why Choose EMEL VPN</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience unparalleled security and privacy with our advanced VPN technology
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 - Tall Card */}
            <div className="bg-gradient-to-br from-white to-teal-50 rounded-3xl p-12 shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Military-Grade Encryption</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide. Every connection is secured with the highest level of cryptographic protection.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span className="text-gray-700 text-sm">AES-256 bit encryption</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span className="text-gray-700 text-sm">Perfect forward secrecy</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span className="text-gray-700 text-sm">Secure protocols</span>
                </li>
              </ul>
            </div>

            {/* Feature 2 & 3 - Stacked Cards */}
            <div className="space-y-8">
              <div className="bg-teal-500 rounded-3xl p-8 text-white shadow-lg hover:shadow-2xl transition-shadow">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mb-5">
                  <i className="ri-lock-line text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">No-Logs Policy</h3>
                <p className="text-white/90 text-sm leading-relaxed">
                  We don't track, collect, or store your online activity. Your privacy is guaranteed with our strict no-logs policy.
                </p>
              </div>

              <div className="bg-gray-50 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-teal-500 rounded-full flex items-center justify-center mb-5">
                  <i className="ri-global-line text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Global Server Network</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Access content from anywhere with our worldwide network of high-speed servers across 50+ countries.
                </p>
              </div>
            </div>

            {/* Feature 4 - Medium Card */}
            <div className="bg-gray-900 rounded-3xl p-12 text-white shadow-lg hover:shadow-2xl transition-shadow">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-flashlight-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Lightning Fast Speed</h3>
              <p className="text-white/90 leading-relaxed mb-8">
                Experience blazing-fast connection speeds without compromising security. Stream, download, and browse without limits.
              </p>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm">Connection Speed</span>
                  <span className="text-teal-400 font-bold">10 Gbps</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm">Uptime</span>
                  <span className="text-teal-400 font-bold">99.9%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Features Grid */}
          <div className="grid md:grid-cols-4 gap-6 mt-12">
            {[
              { icon: 'ri-smartphone-line', title: 'Multi-Device Support', desc: 'Use on all your devices simultaneously' },
              { icon: 'ri-eye-off-line', title: 'Anonymous Browsing', desc: 'Hide your IP and browse privately' },
              { icon: 'ri-download-cloud-line', title: 'Unlimited Bandwidth', desc: 'No data caps or speed throttling' },
              { icon: 'ri-customer-service-line', title: '24/7 Support', desc: 'Expert help whenever you need it' }
            ].map((feature, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-teal-500 transition-colors">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-teal-500 text-2xl`}></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2 text-sm">{feature.title}</h4>
                <p className="text-gray-600 text-xs leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Mission Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-teal-500 text-xs font-bold uppercase tracking-wider mb-6">
                Military-Grade Encryption
              </div>
              <h2 className="text-5xl font-bold text-gray-900 leading-tight mb-8">
                Protecting Your<br />Privacy is Our<br />Mission
              </h2>
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  At EMEL S.R.L., we believe that online privacy is a fundamental right. Our VPN service is built on the foundation of military-grade encryption standards, ensuring that your data remains completely secure from prying eyes.
                </p>
                <p>
                  We maintain a strict no-logs policy, meaning we never track, collect, or store your browsing activity. Your digital footprint is yours alone, and we're committed to keeping it that way.
                </p>
                <p>
                  With servers strategically located across the globe, you can access content from anywhere while maintaining complete anonymity and enjoying lightning-fast connection speeds.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-8 mt-12 pt-12 border-t border-gray-200">
                <div>
                  <div className="text-4xl font-bold text-teal-500 mb-2">256-bit</div>
                  <div className="text-sm text-gray-600">Encryption</div>
                </div>
                <div className="border-l border-gray-200 pl-8">
                  <div className="text-4xl font-bold text-teal-500 mb-2">50+</div>
                  <div className="text-sm text-gray-600">Countries</div>
                </div>
                <div className="border-l border-gray-200 pl-8">
                  <div className="text-4xl font-bold text-teal-500 mb-2">99.9%</div>
                  <div className="text-sm text-gray-600">Uptime</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-100 to-teal-50 rounded-3xl transform rotate-3"></div>
              <div className="relative bg-white rounded-3xl p-12 shadow-xl">
                <div className="space-y-6">
                  {[
                    { icon: 'ri-shield-check-line', title: 'Zero-Knowledge Architecture', color: 'teal' },
                    { icon: 'ri-lock-password-line', title: 'Kill Switch Protection', color: 'blue' },
                    { icon: 'ri-radar-line', title: 'DNS Leak Prevention', color: 'teal' },
                    { icon: 'ri-shield-star-line', title: 'Split Tunneling', color: 'blue' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className={`w-12 h-12 bg-${item.color}-500 rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <i className={`${item.icon} text-white text-xl`}></i>
                      </div>
                      <span className="font-semibold text-gray-900">{item.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Get in Touch</h2>
            <p className="text-lg text-gray-600">
              Have questions? We're here to help you secure your digital life
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-12 mb-12">
            <form id="contact-form" data-readdy-form action="https://readdy.ai/api/form/d5192hlmdo3ncn62hc6g" method="POST" onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              
              // Validate
              const name = formData.get('name') as string;
              const email = formData.get('email') as string;
              const message = formData.get('message') as string;
              
              if (!name || !email || !message) {
                alert('Please fill in all required fields');
                return;
              }

              // Submit
              fetch('https://readdy.ai/api/form/d5192hlmdo3ncn62hc6g', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(formData as any).toString()
              }).then(response => {
                if (response.ok) {
                  alert('Thank you! Your message has been sent successfully.');
                  form.reset();
                } else {
                  alert('Something went wrong. Please try again.');
                }
              }).catch(() => {
                alert('Something went wrong. Please try again.');
              });
            }}>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  placeholder="+1 234 567 8900"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <input
                  type="text"
                  name="subject"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  placeholder="How can we help?"
                />
              </div>

              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="Tell us more about your inquiry..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                className="w-full bg-teal-500 text-white py-4 rounded-lg font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Send Message
              </button>
            </form>
          </div>

          {/* Contact Info Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center hover:border-teal-500 transition-colors">
              <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-teal-500 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Email</h4>
              <a href="mailto:contact@emel-srl.com" className="text-gray-600 text-sm hover:text-teal-500 transition-colors cursor-pointer">
                contact@emel-srl.com
              </a>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center hover:border-teal-500 transition-colors">
              <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-teal-500 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Phone</h4>
              <a href="tel:+13146893771" className="text-gray-600 text-sm hover:text-teal-500 transition-colors cursor-pointer">
                +1 3146893771
              </a>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center hover:border-teal-500 transition-colors">
              <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-teal-500 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Address</h4>
              <p className="text-gray-600 text-sm">
                AV JAURETCHE 5505<br />Posadas, N3300PLO
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Policy Section */}
      <section id="privacy" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="border-l-4 border-teal-500 pl-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-5xl font-bold text-gray-900">Privacy Policy</h2>
              <span className="text-sm text-gray-500 italic">Last updated: January 2025</span>
            </div>

            <div className="space-y-12">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Information We Collect</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    EMEL S.R.L. is committed to protecting your privacy. We maintain a strict no-logs policy, which means we do not track, collect, or store your online activity, browsing history, or connection logs.
                  </p>
                  <p>
                    We only collect minimal information necessary to provide our VPN service, including:
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg mt-0.5"></i>
                      <span>Account email address for authentication and support</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg mt-0.5"></i>
                      <span>Payment information (processed securely by third-party providers)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg mt-0.5"></i>
                      <span>Anonymous analytics to improve service performance</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">How We Use Your Information</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    The limited information we collect is used solely to:
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg mt-0.5"></i>
                      <span>Provide and maintain our VPN service</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg mt-0.5"></i>
                      <span>Process payments and manage subscriptions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg mt-0.5"></i>
                      <span>Respond to customer support inquiries</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg mt-0.5"></i>
                      <span>Improve our service quality and performance</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Data Security</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    We implement industry-leading security measures to protect your data, including AES-256 encryption, secure protocols, and regular security audits. Your connection is protected with military-grade encryption standards.
                  </p>
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 my-4">
                    <p className="text-gray-700 font-medium">
                      <i className="ri-shield-check-line text-teal-500 mr-2"></i>
                      We never sell, rent, or share your personal information with third parties for marketing purposes.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Your Rights</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    You have the right to access, update, or delete your personal information at any time. You can also request a copy of the data we hold about you. Contact us at contact@emel-srl.com for any privacy-related requests.
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">5</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Changes to This Policy</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Terms of Service Section */}
      <section id="terms" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="border-l-4 border-teal-500 pl-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-5xl font-bold text-gray-900">Terms of Service</h2>
              <span className="text-sm text-gray-500 italic">Last updated: January 2025</span>
            </div>

            <div className="space-y-12">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Acceptance of Terms</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    By accessing and using EMEL S.R.L. VPN services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Service Description</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    EMEL S.R.L. provides Virtual Private Network (VPN) services that encrypt your internet connection and protect your online privacy. Our service includes:
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg mt-0.5"></i>
                      <span>Secure encrypted connections</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg mt-0.5"></i>
                      <span>Access to global server network</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg mt-0.5"></i>
                      <span>Multi-device support</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg mt-0.5"></i>
                      <span>24/7 customer support</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Acceptable Use Policy</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    You agree to use our VPN service only for lawful purposes. Prohibited activities include:
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-500 text-lg mt-0.5"></i>
                      <span>Illegal activities or content distribution</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-500 text-lg mt-0.5"></i>
                      <span>Hacking, network attacks, or malicious activities</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-500 text-lg mt-0.5"></i>
                      <span>Spam or unsolicited communications</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-500 text-lg mt-0.5"></i>
                      <span>Violation of intellectual property rights</span>
                    </li>
                  </ul>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-6 my-4">
                    <p className="text-gray-700 font-medium">
                      <i className="ri-alert-line text-red-500 mr-2"></i>
                      Violation of these terms may result in immediate termination of your account without refund.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Payment and Refunds</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    All payments are processed securely through our payment providers. Subscriptions are billed according to your chosen plan. We offer a 30-day money-back guarantee for new customers.
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">5</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Limitation of Liability</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    EMEL S.R.L. provides the VPN service "as is" without warranties of any kind. We are not liable for any damages arising from the use or inability to use our service, including but not limited to data loss, service interruptions, or security breaches beyond our control.
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">6</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Termination</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    You may cancel your subscription at any time through your account settings. We reserve the right to suspend or terminate accounts that violate these terms without prior notice.
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">7</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Changes to Terms</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    We may modify these Terms of Service at any time. Continued use of our service after changes constitutes acceptance of the new terms. We will notify users of significant changes via email.
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">8</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Contact Information</h3>
                </div>
                <div className="ml-14 text-gray-600 leading-relaxed space-y-4">
                  <p>
                    For questions about these Terms of Service, please contact us at contact@emel-srl.com or call +1 3146893771.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Support Center</h2>
            <p className="text-lg text-gray-600">
              Get help with your VPN service anytime, anywhere
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white rounded-3xl p-10 shadow-lg">
              <div className="w-16 h-16 bg-teal-500 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">24/7 Customer Support</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our dedicated support team is available around the clock to assist you with any questions or technical issues. Get expert help whenever you need it.
              </p>
              <div className="space-y-3">
                <a href="mailto:contact@emel-srl.com" className="flex items-center gap-3 text-teal-500 hover:text-teal-600 transition-colors cursor-pointer">
                  <i className="ri-mail-line text-xl"></i>
                  <span className="font-medium">contact@emel-srl.com</span>
                </a>
                <a href="tel:+13146893771" className="flex items-center gap-3 text-teal-500 hover:text-teal-600 transition-colors cursor-pointer">
                  <i className="ri-phone-line text-xl"></i>
                  <span className="font-medium">+1 3146893771</span>
                </a>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-10 shadow-lg text-white">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-book-open-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Knowledge Base</h3>
              <p className="text-white/90 mb-6 leading-relaxed">
                Browse our comprehensive guides and tutorials to get the most out of your VPN service. Learn about setup, troubleshooting, and advanced features.
              </p>
              <button className="bg-white text-teal-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                Browse Articles
              </button>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-3xl p-12 shadow-lg">
            <h3 className="text-3xl font-bold text-gray-900 mb-10 text-center">Frequently Asked Questions</h3>
            <div className="space-y-6">
              {[
                {
                  q: 'How do I set up the VPN on my device?',
                  a: 'Download our app from your device\'s app store, log in with your credentials, and click connect. Our apps are available for Windows, Mac, iOS, Android, and Linux.'
                },
                {
                  q: 'Can I use the VPN on multiple devices?',
                  a: 'Yes! Your subscription allows you to use the VPN on multiple devices simultaneously. Connect all your devices for complete protection.'
                },
                {
                  q: 'What if I experience connection issues?',
                  a: 'Try switching to a different server location, check your internet connection, or restart the app. If issues persist, contact our 24/7 support team for assistance.'
                },
                {
                  q: 'Is my data really private?',
                  a: 'Absolutely. We maintain a strict no-logs policy and use military-grade AES-256 encryption. We never track, collect, or store your browsing activity.'
                },
                {
                  q: 'How do I cancel my subscription?',
                  a: 'You can cancel anytime through your account settings. We offer a 30-day money-back guarantee for new customers if you\'re not satisfied.'
                }
              ].map((faq, index) => (
                <div key={index} className="border-b border-gray-200 pb-6 last:border-0">
                  <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-start gap-3">
                    <i className="ri-question-line text-teal-500 text-xl mt-0.5"></i>
                    {faq.q}
                  </h4>
                  <p className="text-gray-600 ml-8 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            {/* Brand Column */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/1e75ead3-972b-4521-afb5-18f9884529eb.png" alt="EMEL S.R.L." className="h-10 w-10 object-contain" />
                <span className="text-xl font-bold">EMEL S.R.L.</span>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                Protecting your digital freedom with enterprise-grade VPN security. Your privacy is our mission.
              </p>
            </div>

            {/* Newsletter Column */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider mb-6">Stay Updated</h4>
              <p className="text-white/60 text-sm mb-4">Get security tips and updates</p>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-teal-500"
                />
                <button className="bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Quick Links Column */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider mb-6">Quick Links</h4>
              <div className="space-y-3">
                {[
                  { id: 'features', label: 'Features' },
                  { id: 'contact', label: 'Contact' },
                  { id: 'privacy', label: 'Privacy Policy' },
                  { id: 'terms', label: 'Terms of Service' }
                ].map(link => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="block text-white/60 hover:text-teal-400 transition-colors text-sm cursor-pointer"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Column */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider mb-6">Contact</h4>
              <div className="space-y-3">
                <a href="mailto:contact@emel-srl.com" className="flex items-start gap-3 text-white/60 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                  <i className="ri-mail-line text-teal-500 mt-0.5"></i>
                  <span>contact@emel-srl.com</span>
                </a>
                <a href="tel:+13146893771" className="flex items-start gap-3 text-white/60 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                  <i className="ri-phone-line text-teal-500 mt-0.5"></i>
                  <span>+1 3146893771</span>
                </a>
                <div className="flex items-start gap-3 text-white/60 text-sm">
                  <i className="ri-map-pin-line text-teal-500 mt-0.5"></i>
                  <span>AV JAURETCHE 5505<br />Posadas, N3300PLO</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-white/40 text-sm">
                © 2025 EMEL S.R.L. All rights reserved.
              </p>
              <a 
                href="https://readdy.ai/?origin=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/40 text-sm hover:text-teal-400 transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
